/**
 * The onboarding plans' renderer, in one place (`spec-onboarding-3d-plan-renderer-switch`, R1;
 * `spec-onboarding-3d-plan-konva-removal`, R3).
 *
 * Every plan pane below, read-only and editable, draws with the 3D renderer, anchored by the page's
 * twin record (`PlanSceneProviders`, a chunk of its own). The address chooses nothing: a
 * `planRenderer` parameter of any value is ignored.
 *
 * `preloadPlanSceneProviders` starts the chunk's load early (the wizard calls it on mount) and the
 * lazy component takes the same promise, so the chunk is fetched once.
 */
import { lazy, Suspense } from 'react';
import type { ComponentType, ReactNode } from 'react';
import type { AdminTwin } from '@repo/frontend-types';
import type { PlanSceneProvidersProps } from './PlanSceneProviders';
import type { PlanStageProps } from './PlanStage';
import { PlanStageTunnel } from './PlanStageTunnel';
import type { StageTunnelStore } from './PlanStageTunnel';

type ProvidersModule = {
  default: ComponentType<PlanSceneProvidersProps>;
  /**
   * The onboarding stage (`spec-onboarding-persistent-scene-stage`, R1.4). Both real modules export
   * it; optional because a module without one (the page suites' stubbed failed providers) must still
   * resolve to something.
   */
  PlanStage?: ComponentType<PlanStageProps>;
};

let warnedLoadFailed = false;

/**
 * The renderer's chunk, or the failed providers if it cannot be had (a deploy that replaced the
 * chunk under an open tab, a dropped connection): every pane then says the floor plan could not be
 * drawn (`konva-removal` R4). A failed load never reaches an error boundary above the wizard, so only
 * the plan is lost. Warned once per page, with the error.
 *
 * The failed providers are bundled with this module (`webpackMode: "eager"`), so showing them needs
 * no fetch of their own; and they are only evaluated once the renderer has failed, so a page that
 * never fails never runs them.
 */
export const loadPlanSceneProviders = (
  load: () => Promise<ProvidersModule> = () => import('./PlanSceneProviders'),
): Promise<ProvidersModule> =>
  load().catch(error => {
    if (!warnedLoadFailed) {
      warnedLoadFailed = true;
      console.warn('[PlanScene] the 3D renderer could not load', error);
    }
    return import(/* webpackMode: "eager" */ './PlanSceneLoadFailedProviders');
  });

let preloaded: Promise<ProvidersModule> | null = null;

/** The renderer's chunk, loaded once per page however often it is asked for (R1.5). */
export const preloadPlanSceneProviders = (): Promise<ProvidersModule> =>
  (preloaded ??= loadPlanSceneProviders());

const PlanSceneProviders = lazy(preloadPlanSceneProviders);

export interface PlanRendererProvidersProps {
  /** The page's twin record: `undefined` while still being read, `null` for no record. */
  twinRecord: AdminTwin | null | undefined;
  twinBid: string | undefined;
  /**
   * The onboarding stage's tunnel, held by the page above this element's key, so every remount
   * below offers its layer to the same stage (`spec-onboarding-persistent-scene-stage`).
   */
  stageTunnel?: StageTunnelStore;
  children?: ReactNode;
}

export const PlanRendererProviders = ({
  twinRecord,
  twinBid,
  stageTunnel,
  children,
}: PlanRendererProvidersProps) => {
  // Nothing below draws until the renderer has loaded, so no pane mounts with no renderer first.
  return (
    <Suspense fallback={null}>
      <PlanSceneProviders twinRecord={twinRecord} twinBid={twinBid}>
        {stageTunnel ? <PlanStageTunnel store={stageTunnel}>{children}</PlanStageTunnel> : children}
      </PlanSceneProviders>
    </Suspense>
  );
};

/** No stage: what a module without one gives (the page suites' stubbed failed providers). */
const NoPlanStage: ComponentType<PlanStageProps> = () => null;

const LazyPlanStage = lazy(() =>
  preloadPlanSceneProviders().then(module => ({
    // A lazy component resolving to nothing would throw past the wizard, which has no boundary.
    default: module.PlanStage ?? NoPlanStage,
  })),
);

/**
 * The onboarding stage (`spec-onboarding-persistent-scene-stage`, R1.4), through the same chunk and
 * the same promise as the providers, so the chunk is still fetched once. In a lazy boundary of its
 * own, never keyed: rendered above the draft key, its load happens once and never remounts it.
 */
export const PlanStageBoundary = (props: PlanStageProps) => (
  <Suspense fallback={null}>
    <LazyPlanStage {...props} />
  </Suspense>
);
